import { DateTime, IANAZone } from 'luxon';

export const isToday = (date: DateTime, timeZone: string): boolean => {
  return date.toISODate() === DateTime.local({ zone: timeZone }).toISODate();
};

export const isMidnight = (date: DateTime): boolean => {
  return date.hour === 0 && date.minute === 0 && date.second === 0;
};

export const isAllDay = (startDate: DateTime, endDate: DateTime): boolean => {
  return (
    isMidnight(startDate) &&
    isMidnight(endDate) &&
    endDate.equals(startDate.plus({ days: 1 }))
  );
};

export const isMultiDay = (startDate: DateTime, endDate: DateTime): boolean => {
  if (+startDate === +endDate) {
    return false;
  }
  return isMidnight(endDate)
    ? !areDaysEqual(startDate, endDate.minus({ days: 1 }))
    : !areDaysEqual(startDate, endDate);
};

/**
 * All-day events follow Google Calendar's exclusive end-date convention: a
 * single-day event on T spans [T, T+1). An end at or before the start has no
 * meaning under that convention — Google rejects a zero-length all-day event on
 * export, and readers that subtract the day back to find the last covered day
 * would render an inverted range. Hold the invariant when decoding an event and
 * when building a write payload instead of trusting every producer of a date to
 * have preserved it.
 *
 * Only apply this to all-day events. A zero-length timed event is legitimate.
 */
export const toExclusiveEndDate = (
  startDate: DateTime,
  endDate: DateTime
): DateTime =>
  endDate > startDate ? endDate : startDate.startOf('day').plus({ days: 1 });

export const applyTimeToDate = (
  dateReceivingTime: DateTime,
  dateWithTimeToApply: DateTime
): DateTime => {
  return dateReceivingTime.set({
    hour: dateWithTimeToApply.hour,
    minute: dateWithTimeToApply.minute,
    second: dateWithTimeToApply.second,
  });
};

/**
 * Get a DateTime that represents the start of the week.
 *
 * Note:
 * - Luxon hard-codes the start of the week to Monday
 * - Luxon doesn't respect locale settings so neither do we (yet).
 *   This logic always returns a DateTime on Sunday.
 */
export const getStartOfWeek = (date: DateTime): DateTime => {
  return date.weekday === 7
    ? date.startOf('day')
    : date.startOf('week').minus({ day: 1 });
};

/**
 * The experience-level "Week starts on" setting. Matches the persisted
 * `experience.weekStartDay` field: 0 = Sunday, 1 = Monday. Anything other than
 * 1 is treated as Sunday (the app-wide default).
 */
export type WeekStartDay = number;

/**
 * Luxon weekday number for the first day of the week (1 = Monday, 7 = Sunday).
 * Used by the shared Calendar component's `firstDayOfWeek` prop.
 */
export const weekStartToWeekday = (weekStartDay: WeekStartDay): 1 | 7 =>
  weekStartDay === 1 ? 1 : 7;

/**
 * String form of the week-start setting used by the printable grouping options.
 */
export const weekStartToString = (
  weekStartDay: WeekStartDay
): 'sunday' | 'monday' => (weekStartDay === 1 ? 'monday' : 'sunday');

/**
 * Start of the week containing `date`, honoring the week-start setting
 * (0 = Sunday, 1 = Monday). Monday uses Luxon's native ISO week; Sunday
 * delegates to `getStartOfWeek`.
 */
export const startOfWeekFor = (
  date: DateTime,
  weekStartDay: WeekStartDay
): DateTime =>
  weekStartDay === 1 ? date.startOf('week') : getStartOfWeek(date);

/**
 * End of the week (inclusive, end-of-day) containing `date`, honoring the
 * week-start setting.
 */
export const endOfWeekFor = (
  date: DateTime,
  weekStartDay: WeekStartDay
): DateTime =>
  startOfWeekFor(date, weekStartDay).plus({ days: 6 }).endOf('day');

/**
 * Parse an ISO-8601 date in the specified zone.
 */
export const dateInZone = (
  date: string,
  authoredTimeZone: string,
  scheduleTimeZone: string
): DateTime => {
  return DateTime.fromISO(date, { zone: authoredTimeZone }).setZone(
    scheduleTimeZone
  );
};

export const areDaysEqual = (date1: DateTime, date2: DateTime): boolean => {
  return +date1.startOf('day') === +date2.startOf('day');
};

export const areDatesSame = (date1: DateTime, date2: DateTime): boolean => {
  return date1.toMillis() === date2.toMillis();
};

export const areTimesInSameMeridian = (
  date1: DateTime,
  date2: DateTime
): boolean => {
  return date1.hour < 12 === date2.hour < 12;
};

export const datePartInZone = (
  date: DateTime | string,
  zone: string
): DateTime => {
  if (typeof date === 'string') {
    date = DateTime.fromISO(date);
  }
  return DateTime.fromISO(date.toISODate(), { zone });
};

/**
 * Coerce a time zone name into one luxon can safely use. Some misconfigured
 * systems report a zone that isn't a valid IANA zone (e.g. "Etc/Unknown" from
 * `Intl.DateTimeFormat().resolvedOptions().timeZone`). Because we enable
 * `Settings.throwOnInvalid`, passing such a value to luxon throws and crashes
 * rendering (Sentry WEB-CLIENT-265). Fall back to UTC when the zone is missing
 * or unsupported.
 */
export const resolveTimeZone = (
  zoneName: string | null | undefined
): string => {
  return zoneName && IANAZone.isValidZone(zoneName) ? zoneName : 'UTC';
};

export const getLocalTimeZone = (): string => {
  const { zoneName } = DateTime.local();
  return resolveTimeZone(zoneName);
};

export const getDateCardinal = (day: number) => {
  if (day > 3 && day < 21) {
    return 'th';
  }

  switch (day % 10) {
    case 1:
      return 'st';
    case 2:
      return 'nd';
    case 3:
      return 'rd';
    default:
      return 'th';
  }
};

// Parse a stored date string and normalize it to midnight UTC, so a calendar
// date the server holds as a bare date compares as the same instant wherever
// it is read. Returns null for null, undefined, empty, or invalid input.
export const parseIsoDate = (
  dateStr: string | null | undefined
): DateTime | null => {
  if (!dateStr) {
    return null;
  }
  const parsed = DateTime.fromISO(dateStr, { zone: 'utc' });
  if (!parsed.isValid) {
    return null;
  }
  const isoDate = parsed.toISODate();

  return isoDate ? DateTime.fromISO(isoDate, { zone: 'utc' }) : null;
};

export const isDateOutsideRange = (
  date: DateTime,
  range: { startDate: DateTime; endDate: DateTime }
): boolean => {
  const startOfDate = date.startOf('day');
  return (
    startOfDate < range.startDate.startOf('day') ||
    startOfDate > range.endDate.startOf('day')
  );
};

// Chrome and Firefox default the year to 2001 if a year cannot be determined
// from the user's input. new Date('2/1') returns 2001-02-01. In this case,
// default it to the current year instead.
// https://github.com/Hacker0x01/react-datepicker/issues/3586
export const maybeAdjustDefaultYear = (date: DateTime): DateTime => {
  return date.year === 2001 ? date.set({ year: DateTime.local().year }) : date;
};

export const createEntryDate = (
  date: string,
  authoredTimeZone: string,
  scheduleTimeZone: string,
  isOnDay: boolean
): DateTime => {
  // The date string and both zones here come straight from server data and are
  // occasionally malformed — an unsupported IANA zone, or an out-of-range /
  // unparsable date. Because we run luxon with `Settings.throwOnInvalid` (see
  // src/index.tsx), any such value throws; and since this runs while decorating
  // every event, a single bad event would crash the whole page/schedule render
  // (Sentry WEB-CLIENT-26T, sibling of the zone crash WEB-CLIENT-265). Sanitize
  // the zones and fall back to a stable sentinel when the date can't be parsed,
  // so one bad event degrades gracefully instead of taking down the view.
  const authoredZone = resolveTimeZone(authoredTimeZone);
  const scheduleZone = resolveTimeZone(scheduleTimeZone);

  try {
    // Special handling for all day events because the `startDate` for these
    // events are stored with time (midnight UTC) and creating a `DateTime`
    // with a time part will cause the date to shift. Removing the time part
    // allows the `DateTime` to be created in the right zone without time
    // influencing the date. Longer term, we should store the start/end date
    // for all day items without time which is what Google does.
    if (isOnDay) {
      return DateTime.fromISO(date.replace(/T.*$/, ''), {
        zone: scheduleZone,
      });
    }

    return dateInZone(date, authoredZone, scheduleZone);
  } catch {
    // Unparsable/out-of-range date from bad server data. Return a valid sentinel
    // (the Unix epoch in the resolved zone) so `throwOnInvalid` can't crash the
    // render; a garbage date sorts to the far past and drops out of upcoming
    // views instead of surfacing as a broken event.
    return DateTime.fromMillis(0, { zone: scheduleZone });
  }
};

export const getRelativeYears = (
  date: DateTime,
  before: number,
  after: number
): number[] => {
  const base = date.year;
  const start = base - before;
  const end = base + after;

  return new Array(end - start + 1).fill(0).map((_, i) => start + i);
};

export const hasSameMeridiem = (a: DateTime, b: DateTime): boolean => {
  return a.hour < 12 === b.hour < 12;
};
